import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useDebounceFn, useEventListener } from 'ahooks'
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash-es'

import { Personal } from '@/components/Personal'
import { ISessionListItem } from '@/httpRequest/modules/mesperyIm/sessionList'
import { sessionUpdateLastReadAt } from '@/httpRequest/modules/mesperyIm/sessionUpdateLastReadAt'
import { useMessageAtom } from '@/pages/MesperyIM/store/message'
import { useUserAtom } from '@/store/user'
import personal from '@img/personal/Default@3x.png'

import useSWRSessionList from '../hooks/useSWRSessionList'
import styles from '../message.module.less'
import { EMessageType } from '../types/message'

export interface IMessageProps {
  rootStyle?: React.CSSProperties
}

export const Messages: FC<IMessageProps> = (props) => {
  const { rootStyle } = props
  const {
    currentConversation,
    setConversation,
    conversationList,
    setConversationList,
    messageData,
    setMessageData,
    searchKeyword
  } = useMessageAtom()

  const { data, setSize, size } = useSWRSessionList()
  const { user } = useUserAtom()

  const contactListRef = useRef<HTMLDivElement>(null)
  const [openChatUpadte, setOpenChatUpadte] = useState(false)

  const handleUpdateLastReadAt = (last_read_at: number) => {
    sessionUpdateLastReadAt({
      last_read_at: last_read_at,
      session_id: currentConversation?.session_id || '',
      session_type: currentConversation?.session_type || 0
    })
    const sessionList = conversationList?.map((item) => {
      if (item.session_id === currentConversation?.session_id) {
        item.last_read_at = last_read_at
      }
      return item
    })
    setConversationList(sessionList)
  }

  const openChat = (chatData: ISessionListItem) => {
    if (chatData.session_id === currentConversation?.session_id) {
      return
    }
    setOpenChatUpadte(true)

    if (messageData.list.length) {
      // 如果当前会话离开时有消息的话，更新最后一条消息阅读记录
      handleUpdateLastReadAt(
        Number(messageData?.list[messageData.list.length - 1]?.created_at)
      )
    }

    setMessageData({ list: [], next: false })
    setConversation(chatData)
  }

  const handleLoadMore = () => {
    if (conversationList?.length === data?.[0]?.data.count) {
      return
    }
    setSize(size + 1)
  }

  useEffect(() => {
    if (messageData.list.length && currentConversation && openChatUpadte) {
      // 打开会话框时更新最后一条消息阅读时间
      handleUpdateLastReadAt(
        Number(messageData?.list[messageData.list.length - 1]?.created_at)
      )
      setOpenChatUpadte(false)
    }
  }, [messageData, currentConversation])

  // 防抖滚动处理
  const { run: debouncedHandleScroll } = useDebounceFn(
    () => {
      const element = contactListRef.current
      if (!element) return

      // 使用元素自身尺寸计算
      const scrollTop = element.scrollTop
      const clientHeight = element.clientHeight
      const scrollHeight = element.scrollHeight

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        handleLoadMore()
      }
    },
    { wait: 200 }
  )

  useLayoutEffect(() => {
    if (data) {
      const copyData = cloneDeep(data)
      const sessionList = copyData?.reduce((acc, cur, curIndex) => {
        if (curIndex === 0) return cur

        if (cur?.data.list) {
          if (!acc) return cur

          const result = acc.data.list.concat(cur.data.list)
          acc.data.list = result
        }
        return acc
      })
      setConversationList(sessionList?.data.list)
    }
  }, [data])

  const handleRenderListItem = (item: ISessionListItem) => {
    return (
      <li
        key={`${item.session_type}_${item.session_id}`}
        className={`${
          currentConversation?.session_id === item.session_id &&
          currentConversation?.session_type === item.session_type
            ? 'active'
            : ''
        } ${styles.unRead}`}
        onClick={() => openChat(item)}
      >
        {item.session_id !== currentConversation?.session_id &&
        item.last_message_created_at > Number(item.last_read_at) &&
        item.last_message_sender_id !== user.id ? (
          <span className={styles.unreadMessageCount}></span>
        ) : null}
        <Personal
          nickname={item.session_name}
          avatar={item.session_avatar_url || personal}
          extra={
            <div className="intro">
              {item.last_message_type === EMessageType.TxtMsg
                ? item.last_message_content.replaceAll('<br>', '')
                : ''}
              {item.last_message_type === EMessageType.ImgMsg ? 'image' : ''}
              {item.last_message_type === EMessageType.SystemMsg
                ? typeof item.last_message_content === 'string'
                  ? JSON.parse(item.last_message_content).content || ''
                  : (item.last_message_content as Record<string, string>)
                      .content || ''
                : ''}
              {item.last_message_type === 7
                ? (() => {
                    try {
                      if (typeof item.last_message_content === 'string') {
                        const obj = JSON.parse(item.last_message_content)
                        return obj.content || ''
                      } else if (
                        item.last_message_content &&
                        (
                          item.last_message_content as unknown as {
                            content?: string
                          }
                        ).content
                      ) {
                        return (
                          (item.last_message_content as { content?: string })
                            .content || ''
                        )
                      }
                    } catch (e) {
                      return ''
                    }
                    return ''
                  })()
                : ''}
            </div>
          }
          userData={{}}
        />
        <div className={styles.date}>
          {item.last_message_created_at
            ? dayjs(item.last_message_created_at * 1000).format('YYYY/MM/DD')
            : null}
        </div>
      </li>
    )
  }

  useEventListener('scroll', debouncedHandleScroll, { target: contactListRef })

  return (
    <div
      ref={contactListRef}
      className={styles.ContactList}
      style={{ flex: 1, overflowY: 'scroll', ...rootStyle }}
    >
      <ul>
        {conversationList
          ?.filter((item) => {
            return item.session_name.includes(searchKeyword)
          })
          .map((item) => handleRenderListItem(item))}
      </ul>
    </div>
  )
}
